//
//  useTrialQaProgramChecklist.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module provides use Trial QA Program Checklist client behaviour.
//

import { useCallback } from "react";

import {
    GetTrialQaProgramChecklistRequest,
    GetTrialQaProgramChecklistResponse,
    ImportQaProgramChecklistDefaultsRequest,
    InitializeTrialQaProgramChecklistRequest,
    QaProgramChecklistCompletionState,
    ReorderTrialQaProgramChecklistCategoriesRequest,
    ReorderTrialQaProgramChecklistItemsRequest,
    AddTrialQaProgramChecklistNoteRequest,
    SetTrialQaProgramChecklistCompletionRequest,
    TrialQaProgramChecklistItemNote,
    TrialQaProgramChecklistMutationResponse
} from "src/api/Tmd.dtos";
import useApi from "src/hooks/api/useApi";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";

/**
 * Loads a trial checklist and exposes mutations that update the shared SWR cache.
 *
 * Postconditions: Every consumer of a given trial resolves to one SWR key, because the request
 * carries no caller-specific options. That is what keeps the checklist page, the configuration
 * screen and the pinned panel showing the same state. Inactive rows are always returned; callers
 * decide whether to display them.
 *
 * @param trialId Trial whose checklist should be loaded.
 */
const useTrialQaProgramChecklist = (trialId?: number | null) => {
    const client = useServiceStackClient();
    const getRequest = useCallback(
        () =>
            trialId
                ? new GetTrialQaProgramChecklistRequest({ trialId, includeInactive: true })
                : undefined,
        [trialId]
    );
    const response = useApi<
        GetTrialQaProgramChecklistResponse,
        GetTrialQaProgramChecklistResponse["checklist"],
        GetTrialQaProgramChecklistResponse["responseStatus"],
        object
    >("get", getRequest, {
        getData: value => value?.data?.checklist,
        getError: value => value?.data?.responseStatus ?? value?.error?.responseStatus
    });
    const { mutate } = response;

    /** Applies a ServiceStack mutation result to every consumer of this checklist key. */
    const applyMutation = useCallback(
        async (request: Parameters<typeof client.post>[0]) => {
            const result = (await client.post(request)) as TrialQaProgramChecklistMutationResponse;
            await mutate(new GetTrialQaProgramChecklistResponse({ checklist: result.checklist }), {
                // The mutation endpoints always rebuild the view from active rows only, so the
                // response is applied immediately for responsiveness and then refetched to restore
                // any inactive rows it dropped.
                revalidate: true
            });
            return result.checklist;
        },
        [client, mutate]
    );

    const initialize = useCallback(
        () => applyMutation(new InitializeTrialQaProgramChecklistRequest({ trialId: trialId! })),
        [applyMutation, trialId]
    );
    const importDefaults = useCallback(
        (selectedItemIds: number[]) =>
            applyMutation(
                new ImportQaProgramChecklistDefaultsRequest({
                    trialId: trialId!,
                    selectedItemIds
                })
            ),
        [applyMutation, trialId]
    );
    const setCompletionState = useCallback(
        (itemId: number, completionState: QaProgramChecklistCompletionState) =>
            applyMutation(
                new SetTrialQaProgramChecklistCompletionRequest({
                    trialId: trialId!,
                    itemId,
                    completionState
                })
            ),
        [applyMutation, trialId]
    );
    const addNote = useCallback(
        (note: TrialQaProgramChecklistItemNote) =>
            applyMutation(new AddTrialQaProgramChecklistNoteRequest({ trialId: trialId!, note })),
        [applyMutation, trialId]
    );

    const reorderCategories = useCallback(
        (orderedCategoryIds: number[]) =>
            applyMutation(
                new ReorderTrialQaProgramChecklistCategoriesRequest({
                    trialId: trialId!,
                    orderedCategoryIds
                })
            ),
        [applyMutation, trialId]
    );
    const reorderItems = useCallback(
        (categoryId: number, orderedItemIds: number[]) =>
            applyMutation(
                new ReorderTrialQaProgramChecklistItemsRequest({
                    trialId: trialId!,
                    categoryId,
                    orderedItemIds
                })
            ),
        [applyMutation, trialId]
    );

    return {
        ...response,
        initialize,
        importDefaults,
        setCompletionState,
        addNote,
        reorderCategories,
        reorderItems
    };
};

export default useTrialQaProgramChecklist;
